/* 패스워드 게이트: 세로 락업을 유리 3D 로 바꾼다 (v3.9.173, SP 10-02 "유리 질감으로").
   10-06 개정 (SP "팔라스처럼 투명하게 · 워드마크도 3D 로 같이 돌게 · 두께 맞추고 크게 중앙")
   - 락업 모드(cfg.lockup): 심볼+워드마크 한 덩어리(`ks-lockup.glb`, Blender, 두께 동일 0.304)가 한 축으로 함께 돈다.
     캔버스 기준을 2D 심볼이 아니라 SVG 전체로 잡고, 첫 프레임 뒤 2D 워드마크도 숨긴다(.is-3d-lockup).
   - 사각 경계 제거: 배경 영상 판은 transmission 패스(유리가 굴절해 볼 렌더 타깃)에만 그린다.
     본 패스(캔버스)에 그리면 캔버스 사각형 전체가 영상 복사본으로 덮이고, 실제 영상과 프레임·밝기·스크림 밴드가
     어긋나 사각형이 보인다(마스크로 가장자리를 흐려도 남았다).
   - 우윳빛 제거 두 가지: ① 배경 판 셰이더가 컴파일에 실패하고 있었다(`#include` 가 줄 맨 앞이 아니었다).
     ② three 는 VideoTexture 를 sRGB 디코드 없이 올리므로 커스텀 셰이더가 직접 선형화해야 한다.

   구성
   - 2D 심볼 자리에 캔버스를 겹친다. 정면일 때 3D 심볼 윤곽이 2D 심볼과 같은 자리·크기가 되도록 카메라를 맞춘다.
     워드마크는 2D 그대로 둔다(락업 비율·클리어 스페이스 유지).
   - 유리는 뒤를 굴절해야 유리로 보인다. 그래서 배경 영상을 3D 장면 안에도 그린다(캔버스 영역만큼 잘라서).
     HTML 위에서는 전면 스크림(--gate-scrim-base)이 영상을 어둡게 하므로 같은 배율을 곱해 밝기를 맞추고,
     캔버스 가장자리는 마스크로 흐려 이음매를 숨긴다(password-gate.css).
   - 3D 를 그릴 수 없거나 영상을 텍스처로 쓸 수 없으면(교차 출처) 2D 로고를 그대로 둔다. 실패는 조용히 2D 로 돌아간다.

   모션
   - 등장: 한 바퀴 돌고 정면에 내려앉는다(1.8초). 이후 14초에 한 바퀴(10-06 SP "회전 조금 더 빠르게": 0.30 → 0.45 rad/s).
   - 끌어서 돌리기 + 관성. 자동 회전은 **멈추지 않는다**(10-06 SP "클릭하면 멈춘다 → 계속 돌아야 한다").
     끌기·관성은 자동 회전 위에 더해질 뿐이다(누르는 순간 속도를 0 으로 만들지 않는다).
   - 동작 줄이기: 등장·자동 회전 없음(정면 정지). 끌어서 돌리기만 남긴다.
   - 탭이 숨으면 그리기를 멈춘다. */
import * as THREE from 'three';
import { GLTFLoader } from 'ks-three/gltf-loader';

const gate = document.querySelector('.password-gate');
const logo = gate && gate.querySelector('.password-gate__logo');
const symbol = logo && logo.querySelector('.ks-logo-vertical__symbol');
const svg = logo && logo.querySelector('.ks-logo-vertical');
const cfgEl = document.getElementById('ks-gate-3d-config');
let stage, renderer;   /* start() 호출보다 위에 둔다(아래에 두면 TDZ 로 바로 실패했다) */

if (gate && logo && symbol && cfgEl) start(JSON.parse(cfgEl.textContent)).catch((e) => { console.warn('[ks-gate-3d] 2D 로 대체:', e); fallback(); });

function fallback() {
  logo.classList.remove('is-3d', 'is-3d-lockup', 'is-3d-pending');   /* 10-12: 처음부터 숨긴 2D 를 되돌린다 */
  if (stage) stage.remove();
  if (renderer) renderer.dispose();
}

/* 유리용 반사 환경: 어두운 방 + 가늘고 밝은 스트립 조명 몇 줄.
   유리는 '무엇을 비추는가' 로 보인다. 밝고 고른 방(RoomEnvironment)은 면 전체를 하얗게 덮고,
   어두운 방의 가는 조명은 모서리·면에 날카로운 하이라이트 줄만 남긴다(제품 사진 스튜디오와 같은 원리). */
function glassStudio(renderer) {
  const s = new THREE.Scene();
  s.background = new THREE.Color(0x060606);
  const strip = (w, h, pos, k) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(k, k, k), side: THREE.DoubleSide }));
    m.position.set(...pos); m.lookAt(0, 0, 0); s.add(m);
  };
  strip(12, 0.5, [0, 5, -1], 9);      /* 위 가로 스트립: 윗면·베벨에 한 줄 */
  strip(0.45, 8, [-5, 0.5, 1.5], 7);  /* 왼쪽 세로 */
  strip(0.45, 8, [5, 0.5, 1.5], 7);   /* 오른쪽 세로 */
  strip(0.3, 6, [-2.5, 0, 6], 3.5);   /* 앞쪽 가는 줄: 정면에서도 면에 사선 반사 */
  strip(8, 3, [0, 1, -6], 1.2);       /* 뒤 소프트박스: 뒷면이 비칠 때 너무 까맣지 않게 */
  const tex = new THREE.PMREMGenerator(renderer).fromScene(s, 0).texture;
  return tex;
}

async function start(cfg) {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* 캔버스를 맞출 2D 기준: 락업이면 SVG 전체(심볼+워드마크), 아니면 심볼만 */
  const target = cfg.lockup ? svg : symbol;

  /* 무대: 심볼 사각형을 가로 2배·세로 1.8배로 넓힌 영역(회전 중 잘리지 않게, 마스크 여유) */
  stage = document.createElement('div');
  stage.className = 'password-gate__stage';
  stage.setAttribute('aria-hidden', 'true');
  gate.appendChild(stage);

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setClearColor(0x000000, 0);
  stage.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.environment = glassStudio(renderer);
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 200);

  /* 림 라이트 두 개가 유리 모서리에 윤곽을 세운다(데모 '유리' 프리셋 값) */
  /* 직광은 약하게: 유리의 윤곽은 반사 환경(스트립 조명)이 그린다. 직광이 세면 면 전체가 하얗게 뜬다. */
  const key = new THREE.DirectionalLight(0xffffff, 0.4); key.position.set(-2, 3, 4); scene.add(key);
  const rimL = new THREE.DirectionalLight(0xffffff, 1.6); rimL.position.set(-3, 2, -3); scene.add(rimL);
  const rimR = new THREE.DirectionalLight(0xffffff, 1.6); rimR.position.set(3, 2, -3); scene.add(rimR);

  /* 배경 영상 쿼드: 화면을 채우는 평면, 깊이 맨 뒤. 유리(transmission)가 이것을 굴절해 보여준다. */
  const bgUniforms = {
    uMap: { value: null }, uOffset: { value: new THREE.Vector2() }, uScale: { value: new THREE.Vector2(1, 1) },
    uGain: { value: 1 }, uOn: { value: 0 },
  };
  const bg = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({
    uniforms: bgUniforms, depthWrite: false, depthTest: false, toneMapped: false,
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.9999, 1.0); }',
    /* `#include` 를 쓰지 않는다. 지시어는 줄 맨 앞이어야 하는데 문자열 이어붙이기·압축에서 쉽게 깨진다
       (v3.9.173 은 이 줄이 깨져 셰이더가 컴파일되지 않았다). 이 판은 선형 렌더 타깃에만 그리므로 출력 변환도 필요 없다.
       sRGBTransferEOTF = VideoTexture 선형화(three 는 내장 재질에만 DECODE_VIDEO_TEXTURE 로 보정한다). */
    fragmentShader: 'uniform sampler2D uMap; uniform vec2 uOffset; uniform vec2 uScale; uniform float uGain; uniform float uOn; varying vec2 vUv;\n'
      + 'void main(){ if (uOn < 0.5) discard; vec4 c = sRGBTransferEOTF(texture2D(uMap, uOffset + vUv * uScale)); gl_FragColor = vec4(c.rgb * uGain, 1.0); }',
  }));
  bg.frustumCulled = false; bg.renderOrder = -1; scene.add(bg);
  /* transmission 패스(렌더 타깃 있음)에서만 켠다. 본 패스(캔버스)에서는 버려 사각 경계가 생기지 않게 한다. */
  bg.onBeforeRender = (r) => { bgUniforms.uOn.value = bgUniforms.uMap.value && r.getRenderTarget() ? 1 : 0; bg.material.uniformsNeedUpdate = true; };

  const pivot = new THREE.Group(); scene.add(pivot);
  const gltf = await new GLTFLoader().loadAsync(cfg.glb);
  const model = gltf.scene;
  /* 유리 v2 (SP 10-02 "유리 질감 더"): v1 은 부드러운 흰 스튜디오 반사가 면 전체를 덮어 흰 플라스틱처럼 보였다.
     - 거칠기 0 · 두께 2.2 · IOR 1.52 = 뒤 영상이 눈에 띄게 휘어 보인다
     - dispersion = 모서리에서 빛이 갈라지는 무지개 테(유리를 유리로 읽게 하는 가장 강한 신호)
     - attenuation = 두꺼운 곳일수록 아주 옅은 무채색 회색(맑은 판유리의 두께감). 색상(hue)은 0: 로고 색 입히기 금지 규칙.
     - 반사 세기는 1, 대신 환경 자체가 어두워 반사는 스트립 조명 몇 줄로만 맺힌다
     유리 v3 (SP 10-06 "팔라스처럼 투명하게"): 뒤 영상이 원본과 같은 톤으로 비치고 형태는 모서리 반사로만 읽힌다.
     - toneMapped false = 굴절된 영상에 ACES 가 다시 걸려 탁해지는 것을 막는다(HTML 영상과 같은 톤)
     - 두께 0.6 · IOR 1.45 · dispersion 0.3 = 왜곡·무지개 테를 줄여 맑게. 감쇠(회색) 없음
     - 반사 0.35 = 스트립 하이라이트는 남기고 면을 덮지 않게 */
  const mat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff, metalness: 0, roughness: 0, transmission: 1, thickness: 0.6, ior: 1.45,
    dispersion: 0.3, attenuationColor: new THREE.Color(0xffffff), attenuationDistance: Infinity,
    specularIntensity: 1, clearcoat: 0, envMapIntensity: 0.35, side: THREE.DoubleSide, toneMapped: false,
  });
  model.traverse((o) => { if (o.isMesh) o.material = mat; });
  const box = new THREE.Box3().setFromObject(model);
  model.position.sub(box.getCenter(new THREE.Vector3()));
  const size = box.getSize(new THREE.Vector3());
  pivot.add(model);

  /* 굴절 배경 소스: 먼저 영상의 첫 장면 이미지(포스터), 영상이 재생 가능해지면 영상으로 바꾼다 (10-12 SP "첫 장면 이미지로 유리 로고 먼저").
     모바일은 영상(수십 MB)이 붙기까지 약 10초라, 그동안 흰 2D 로고를 숨기면 로고 자리가 비었다.
     포스터는 같은 출처(/cdn/shop/files/preview_images/…)라 텍스처로 쓸 수 있고 페이지가 이미 받아 둔 이미지다. */
  let srcW = 0, srcH = 0, posterTex = null, posterLoading = false;
  function findPoster() {
    if (posterTex || posterLoading || video) return;
    const img = [...gate.querySelectorAll('.password-gate__media img')].find((el) => el.offsetParent !== null && (el.currentSrc || el.src));
    if (!img) return;
    let url = img.currentSrc || img.src;
    /* srcset 이 아주 작은 폭(width=8 등 자리표시)을 고른 경우 화면 폭에 맞는 크기로 다시 요청한다 */
    if (/[?&]width=\d+/.test(url)) url = url.replace(/([?&]width=)\d+/, '$1' + Math.min(1600, Math.round(gate.clientWidth * Math.min(window.devicePixelRatio || 1, 2))));
    const im = new Image(); im.crossOrigin = 'anonymous'; im.decoding = 'async';
    posterLoading = true;
    im.onerror = () => { posterLoading = false; };
    im.onload = () => {
      posterLoading = false;
      if (video) return;   /* 그새 영상이 붙었으면 쓰지 않는다 */
      try {
        const c = document.createElement('canvas'); c.width = c.height = 2;
        const x = c.getContext('2d'); x.drawImage(im, 0, 0, 2, 2); x.getImageData(0, 0, 1, 1);   /* 교차 출처 시험(영상과 같은 방식) */
      } catch (e) { return; }
      posterTex = new THREE.Texture(im);
      /* 셰이더가 직접 sRGB→선형 변환을 하므로(영상과 같은 경로) 텍스처는 원본 그대로 올린다. SRGBColorSpace 로 두면 GPU 가 한 번 더 풀어 어두워진다. */
      posterTex.colorSpace = THREE.NoColorSpace; posterTex.needsUpdate = true;
      srcW = im.naturalWidth; srcH = im.naturalHeight;
      bgUniforms.uMap.value = posterTex;
      layout();
      reveal();
    };
    im.src = url;
  }

  /* 영상: 지금 보이는 쪽(데스크톱/모바일)의 <video>. deferred-media 가 늦게 넣으므로 찾을 때까지 다시 본다. */
  let video = null, videoTex = null;
  function findVideo() {
    const v = [...gate.querySelectorAll('.password-gate__media video')].find((el) => el.offsetParent !== null && el.readyState >= 2);
    if (!v || v === video) return;
    /* 교차 출처 영상은 텍스처로 못 쓴다(그리면 예외). 2D 캔버스로 먼저 시험한다. */
    try {
      const c = document.createElement('canvas'); c.width = c.height = 2;
      const x = c.getContext('2d'); x.drawImage(v, 0, 0, 2, 2); x.getImageData(0, 0, 1, 1);
    } catch (e) { return; }
    video = v;
    if (videoTex) videoTex.dispose();
    videoTex = new THREE.VideoTexture(v); videoTex.colorSpace = THREE.SRGBColorSpace;
    bgUniforms.uMap.value = videoTex;   /* uOn 은 패스마다 onBeforeRender 가 정한다 */
    srcW = v.videoWidth; srcH = v.videoHeight;
    if (posterTex) { posterTex.dispose(); posterTex = null; }   /* 포스터 → 영상 전환(첫 프레임이 같아 이음매가 없다) */
    layout();
    reveal();
  }

  /* 배치: 무대 위치·크기, 카메라 거리(정면 윤곽 = 2D 심볼), 배경 잘라내기 */
  function layout() {
    const g = gate.getBoundingClientRect(), r = target.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const w = r.width * 2.0, h = r.height * 1.8;
    const left = r.left - g.left + r.width / 2 - w / 2, top = r.top - g.top + r.height / 2 - h / 2;
    Object.assign(stage.style, { left: left + 'px', top: top + 'px', width: w + 'px', height: h + 'px' });
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
    /* 모델 높이가 캔버스 높이의 1/1.8 로 투영되는 거리 */
    const vFov = THREE.MathUtils.degToRad(camera.fov);
    const dist = (size.y * 1.8) / (2 * Math.tan(vFov / 2)) + size.z / 2;
    camera.position.set(0, 0, dist); camera.lookAt(0, 0, 0);
    /* 배경: object-fit:cover 로 게이트를 채운 영상에서 무대 사각형 부분만 */
    if (srcW && srcH) {
      const W = g.width, H = g.height, vw = srcW, vh = srcH;
      const s = Math.max(W / vw, H / vh), ox = (W - vw * s) / 2, oy = (H - vh * s) / 2;
      bgUniforms.uScale.value.set(w / (vw * s), h / (vh * s));
      bgUniforms.uOffset.value.set((left - ox) / (vw * s), 1 - (top + h - oy) / (vh * s));
    }
    const base = parseFloat(getComputedStyle(gate).getPropertyValue('--gate-scrim-base')) || 0.18;
    bgUniforms.uGain.value = 1 - base;
  }

  /* 상호작용 */
  let rotY = 0, velY = 0, dragging = false, lastX = 0, intro = reduce ? 1 : 0;
  const IDLE = reduce ? 0 : 0.45;
  stage.addEventListener('pointerdown', (e) => { dragging = true; lastX = e.clientX; intro = 1; stage.setPointerCapture(e.pointerId); });
  stage.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const d = (e.clientX - lastX) * 0.012; lastX = e.clientX; rotY += d; velY = d * 60;
  });
  const end = () => { dragging = false; };
  stage.addEventListener('pointerup', end); stage.addEventListener('pointercancel', end);

  const easeOutBack = (t) => 1 + 2.4 * Math.pow(t - 1, 3) + 1.4 * Math.pow(t - 1, 2);
  let last = performance.now(), raf = 0, frames = 0;
  function loop(t) {
    raf = requestAnimationFrame(loop);
    const dt = Math.min(0.05, (t - last) / 1000); last = t;
    if (!video && (frames++ % 30 === 0)) { findVideo(); findPoster(); }   /* 포스터가 늦게 생기는 경우도 다시 본다 */
    if (intro < 1) {
      intro = Math.min(1, intro + dt / 1.8);
      rotY = (1 - easeOutBack(intro)) * -Math.PI * 2;
    } else if (dragging) {
      rotY += IDLE * dt;   /* 누르고 있는 동안에도 자동 회전은 계속(끌기 이동은 pointermove 가 더한다) */
    } else {
      velY *= Math.pow(0.04, dt);
      rotY += (velY + IDLE) * dt;
    }
    pivot.rotation.y = rotY;
    renderer.render(scene, camera);
  }

  /* 공개 시점 = 굴절 배경(포스터 또는 영상) 텍스처가 붙은 뒤 (10-06, 10-12 포스터 추가).
     영상이 붙기 전에는 굴절할 배경이 없어 유리가 **검은 덩어리**로 보인다(390x844 실측: 영상 로드 전 수 초간).
     그래서 영상을 얻을 때까지 무대를 투명하게 두고 2D 로고를 그대로 보인다. 끝내 못 얻으면(교차 출처 등)
     2D 로 남는다 = 위 머리말의 원래 의도와 같다. 등장 회전은 공개 순간부터 시작한다. */
  let revealed = false;
  function reveal() {
    if (revealed) return;
    revealed = true;
    intro = reduce ? 1 : 0;
    renderer.render(scene, camera);   /* 영상이 붙은 첫 프레임을 그린 뒤에 2D 를 숨긴다(빈 순간 없게) */
    stage.classList.add('is-ready');
    logo.classList.add('is-3d');
    if (cfg.lockup) logo.classList.add('is-3d-lockup');
  }

  layout();
  findVideo();
  findPoster();
  raf = requestAnimationFrame(loop);

  const ro = new ResizeObserver(layout); ro.observe(logo); ro.observe(gate);
  window.addEventListener('resize', layout);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cancelAnimationFrame(raf);
    else { last = performance.now(); raf = requestAnimationFrame(loop); }
  });
  renderer.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); cancelAnimationFrame(raf); fallback(); });
}
